import {Component, DestroyRef, effect, Inject, inject, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {asyncScheduler, EMPTY, Observable, of, Subject, timer} from "rxjs";
import {catchError, switchMap, throttleTime} from "rxjs/operators";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {AppRoutes} from "../../../enums/routes";
import {LocationRoutes} from "../_models/_enums/location-routes";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {EventHttpService} from "../../../management/x-http-requests/event-http.service";
import {EventDetailsDto, EventDetailsOrder} from "../../../models/event/event-details";
import {BookingType, BookingTypeLabels} from "../../../models/payment/booking-type";
import * as signalR from "@microsoft/signalr";
import {HubConnectionState} from "@microsoft/signalr";
import {CompanyRoutes} from "../../company/x-models/x-enums/company-routes";
import {PermissionService} from "../../../_auth/permission.service";
import {AuthenticationService} from "../../../_auth/authentication.service";
import {LocationHttpService} from "../../../http-requests/location-http.service";
import {LocationDto} from "../../../models/location-dto";
import {Sortable} from "../../../_abstracts/sortable";
import {Sorting} from 'src/app/x-models/x-enums/sorting';
import {EmployeeHttpService} from "../../company/x-http-requests/employee-http.service";
import {EmployeeOdto} from "../../company/x-models/employee-odto";
import copy from "copy-to-clipboard";
import {LocationConfigLinker} from "../../../models/location/location-config-linker";
import {ConfigurationHttpService} from "../../../http-requests/configuration-http.service";
import {ConfigurationDto} from "../../../models/configuration-dto";
import {SupplierDto} from "../../company/x-models/supplier-dto";
import {HttpEventType} from "@angular/common/http";
import {OrderHelper} from "../_services/order-helper";
import {EventHelper} from "../_services/event-helper";
import {EventRevenueTileComponent} from "./event-revenue-tile/event-revenue-tile.component";
import {SupplierPopupComponent} from "../../company/x-popups/supplier-popup/supplier-popup.component";
import {FormsModule} from "@angular/forms";
import {PageContainerComponent} from "../../../_components/page-container/page-container.component";
import {PageScrollableComponent} from "../../../_components/page-scrollable/page-scrollable.component";
import {CommonModule, CurrencyPipe, DatePipe} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {SortingIndicatorComponent} from "../../../x-components/sorting-indicator/sorting-indicator.component";
import {EventSummaryCardComponent} from "./event-summary-card/event-summary-card.component";
import {
  CompositionGroupingMode,
  compositionGroupingParameter,
  CompositionGroupingPreferenceService,
  CompositionGroupingSettingsComponent
} from "../../../_shared/composition-grouping";
import {VirtualEventDetailsComponent} from "./virtual-event-details.component";
import {EventKind} from "../../../enums/event-kind";
import {CompositionGroupingParameter, EventOrderStats} from "../../../models/event/event-order-stats";
import {EventOrderDelta} from "../../../models/event/event-order-delta";
import {SelectComponent} from "../../../_ui/components/controls/select/select.component";
import {InlineNoticeComponent} from "../../../_ui/components/feedback/inline-notice/inline-notice.component";

/**
 * How long a burst of `TransferEventData` hub notifications is coalesced into a single refetch (Bug #408).
 *
 * During an event's active sale window every booking pushes one notification, so a busy venue produces many
 * per second. Without coalescing each one started its own `getExtendedEvent`; with a round-trip longer than
 * the gap between notifications the page spent the whole burst re-issuing requests it then discarded.
 */
const LIVE_REFRESH_WINDOW_MS = 1000;

/**
 * Base backoff before a FAILED live refresh is retried; the delay doubles per consecutive failure
 * (2s → 4s → 8s). Modest on purpose: the next booking's notification would trigger a refresh anyway, so this
 * only covers the case where the burst's LAST refresh is the one that failed and nothing else arrives.
 */
const LIVE_REFRESH_RETRY_BASE_MS = 2000;

/** How many times a failed live refresh is retried on its own before the page waits for the next notification. */
const LIVE_REFRESH_MAX_RETRIES = 3;

/**
 * The value {@link EventDetailsComponent.pageSize} carries for „Alle Transaktionen" (#716).
 *
 * `0`, not `undefined` or `Infinity`, because it is what goes on the wire: the server reads a non-positive
 * `take` as "every order of the window", which is also the pre-#716 default every other caller still gets.
 */
const PAGE_SIZE_ALL = 0;

/**
 * How many of the NEWEST transactions the table loads by default (#716).
 *
 * The table used to load every order of the event, and — because the four tiles were summed from it — had to.
 * Measured on prev01, that was 2,063 ms of SQL per request, re-run on every single sale from every open
 * client. The tiles now come from server-side aggregates, so the list is free to be a page.
 */
const DEFAULT_PAGE_SIZE = 100;

/**
 * Above this many transactions, the page names the cost of „Alle" before the user picks it (Task #755).
 *
 * Measured on a seeded 10,000-transaction event: the default page (100) loads in ~3.4 s at 167 KB, while
 * „Alle" takes 12–16 s and ships 12.9 MB. There is no single point where the cost turns from "free" to
 * "expensive" — 1,000 is chosen as a round number well below where the measured event turned slow (10,000),
 * comfortably above the default page (100) and every step below it (25/50/100/250/500), so the notice only
 * appears for events large enough that „Alle" is a materially bigger request than the default, not for a
 * quiet event that happens to nudge past the 500 step.
 */
const LARGE_EVENT_TRANSACTION_THRESHOLD = 1000;

/** One entry of the „Angezeigte Transaktionen" picker. */
export interface PageSizeOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-event-details',
  templateUrl: './event-details.component.html',
  imports: [
    CommonModule,
    FormsModule,
    SupplierPopupComponent,
    PageContainerComponent,
    PageScrollableComponent,
    CurrencyPipe,
    RouterLink,
    DatePipe,
    MatTooltip,
    SortingIndicatorComponent,
    EventSummaryCardComponent,
    EventRevenueTileComponent,
    CompositionGroupingSettingsComponent,
    VirtualEventDetailsComponent,
    SelectComponent,
    InlineNoticeComponent
  ],
  styleUrl: './event-details.component.scss'
})
/**
 * The `/location/event/summary` page: four aggregate tiles ("Verkaufspunkt", "Sonstiges",
 * "Transaktionstypen", "Artikel"), the transaction table and the event's sub-flows (employee sales, price
 * profiles, download).
 *
 * **Live-update contract (Bug #408).** While the event is selling, the server pushes `TransferEventData`
 * over the `/hubs/event` SignalR hub on every processed booking and the page refetches the whole extended
 * event. Four rules keep that path from ever rendering a tile as `0`:
 *
 * 1. **One in-flight refetch, newest wins.** Notifications are funnelled through {@link liveRefresh} and
 *    served by a `switchMap`, so a slower older response is cancelled and can never overwrite a newer
 *    snapshot. Previously every notification started its own independent subscription.
 * 2. **The burst is coalesced, but the LAST notification is never swallowed.** `throttleTime` runs with
 *    `trailing: true` — the trap here is that the rxjs default (`{leading: true, trailing: false}`) drops
 *    every notification that arrives inside the window, i.e. exactly the final one of a burst, leaving the
 *    page permanently stale. See the `#408 live refresh` spec suite.
 * 3. **A failed refresh keeps the page.** The live path has its own error handling: it retains the last good
 *    snapshot and retries with backoff, instead of the initial load's `router.navigate` off the page.
 * 4. **Hold-last-good.** A degenerate snapshot (no orders at all, or orders whose compositions are all gone —
 *    literally the payload the torn server-side split query produced) is REFUSED while a populated snapshot
 *    is on screen. This is defence in depth behind the server fix, not a substitute for it.
 *
 * The hub connection itself is idempotent: `queryParams` changes (clicking a "Verkaufspunkt" row sets
 * `location`) re-run the load, and previously each one built a second `HubConnection` over the first, which
 * leaked and kept firing.
 */
export class EventDetailsComponent extends Sortable implements OnInit, OnDestroy {
  protected locationId: string | undefined;
  protected last: number = 10;
  /**
   * How many of the newest transactions the table loads (#716); {@link PAGE_SIZE_ALL} = every one of them.
   *
   * Deliberately component state rather than a query parameter: it is a view preference for one visit, and the
   * page already carries `id`/`location`/`last` in the URL, each of which re-runs `load() → connect()`.
   */
  protected pageSize: number = DEFAULT_PAGE_SIZE;
  protected readonly pageSizeOptions: PageSizeOption[] = [
    {label: '25', value: 25},
    {label: '50', value: 50},
    {label: '100', value: 100},
    {label: '250', value: 250},
    {label: '500', value: 500},
    {label: 'Alle', value: PAGE_SIZE_ALL}
  ];
  protected event: EventDetailsDto | undefined;
  protected location: LocationDto | undefined;
  protected sortedLocationValuePairs: { key: string, name: string, count: number, amount: number }[] = [];
  protected connection: signalR.HubConnection | undefined;
  protected employeeSales_data: EmployeeOdto[] | undefined;
  protected supplierPopup_isVisible: boolean = false;
  protected locationConfig_data: LocationConfigLinker[] | undefined;
  protected locationConfig_configurations: ConfigurationDto[] | undefined;
  protected locationConfig_generalConfiguration: ConfigurationDto | undefined;
  protected readonly views: string[] = ["drinks", "location", "others", "payment"];
  protected view_index: number = 0;
  protected readonly N5BookingType = BookingType;
  protected readonly HubConnectionState = HubConnectionState;
  protected readonly CompanyRoutes = CompanyRoutes;
  protected readonly Routes = AppRoutes;
  protected readonly LocationRoutes = LocationRoutes;
  protected readonly BookingTypeLabels = BookingTypeLabels;
  protected readonly Sorting = Sorting;
  protected readonly EventHelper = EventHelper;
  /** #664 — the template picks the virtual page over this one from the loaded event's kind. */
  protected readonly EventKind = EventKind;
  /**
   * The shared "Kombinationen einzeln" preference (#534) — `protected` because the summary cards and the hero
   * tiles read it (the switch itself lives inside the projected "Anzeigeoptionen" panel, which injects the
   * same service). One `providedIn: 'root'` instance app-wide, so flipping it here is also reflected on the
   * single-event-analysis page.
   */
  protected readonly grouping = inject(CompositionGroupingPreferenceService);
  private readonly destroyRef = inject(DestroyRef);
  /** Every `TransferEventData` notification (and every self-scheduled retry) is pushed in here. */
  private readonly liveRefresh = new Subject<void>();
  /**
   * The live view's position in the event's order change log (#716 / Task #723) — an OPAQUE server token.
   *
   * **This field is written in exactly one place — {@link apply}, the commit point every payload that reaches
   * the screen passes through.** That single rule is what keeps the `switchMap` below safe.
   *
   * Cancelling an in-flight request is harmless when every response is a full, idempotent snapshot — the next
   * one carries everything the cancelled one would have, which is why #408 chose `switchMap` in the first
   * place. It is **data loss** for a delta: the changes in a cancelled response are never re-sent. Because the
   * cursor only ever moves after a response has actually been applied, a cancelled, failed or discarded
   * response leaves it exactly where it was, and the next request asks for the same range again. The
   * cancellation semantics of #408 are therefore preserved unchanged, and nothing is lost.
   *
   * `undefined` means "no delta stream": a virtual event, a pre-#723 server, or a full load that has not
   * landed yet. The page then refetches in full on every notification, i.e. the pre-#723 behaviour.
   */
  private deltaCursor: string | undefined;
  /** The event the current hub connection was opened for — `connect()` reuses it while this still matches. */
  private connectedEventId: string | undefined;
  /** Consecutive failed live refreshes; reset by the first successful one. Drives the retry backoff. */
  private liveRefresh_failures: number = 0;
  /** The "live updates are not arriving" alert is shown once per failure streak, not once per attempt. */
  private liveRefresh_alerted: boolean = false;
  private id: string | undefined;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private gas: GlobalAlertService,
              public PermissionService: PermissionService,
              private authenticationService: AuthenticationService,
              @Inject("BASE_URL") private baseUrl: string,
              private eventHttp: EventHttpService,
              private locationHttp: LocationHttpService,
              private employeeHttp: EmployeeHttpService,
              private configurationHttp: ConfigurationHttpService) {
    super();
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    // #534: re-bucket the two composition summaries when the user flips "Kombinationen einzeln" — and, since
    // #716 / Task #722, REFETCH as well.
    //
    // Both steps, in this order, and the order is the design:
    //
    // 1. **Re-bucket what is in memory, synchronously.** The click stays instant. The snapshot on screen
    //    carries the OTHER grain, so `EventHelper` folds the loaded orders for it — which is page-scoped, and
    //    is exactly what this feature stopped shipping as a final answer. It is fine as a transient: the
    //    TILES never come from it (they read the server aggregate directly), and step 2 replaces the rows.
    //    For the one round trip it lasts, the two composition cards SAY so — see
    //    {@link isCompositionGrainSettling}, which reads that same grain mismatch off the snapshot rather than
    //    being toggled from here, so nothing can leave the affordance on after the rows have settled.
    // 2. **Ask the server for the new grain.** Both grains are now event-wide SQL aggregates and the server
    //    computes the one the request names, because the „einzeln" rows must describe the whole evening —
    //    most users work in that mode, and at the default page of 100 the old client-side fold described the
    //    newest 100 transactions of it. `loader: false`: the rows on screen are already right-ish and a
    //    full-page spinner for a settings toggle is worse than a brief stale row.
    //
    // Still an `effect()` rather than `toObservable(...) | takeUntilDestroyed(...)`: it expresses "when this
    // signal changes, recompute" with no subscription, ReplaySubject or teardown of its own, it runs
    // synchronously rather than a tick after the click, and it is destroyed with the component's own injector.
    effect(() => {
      // Read the signal FIRST and unconditionally, so the dependency is registered even on the initial run
      // when no event is loaded yet (a `return` before the read would leave the effect subscribed to nothing).
      const mode = this.grouping.mode();
      const event = this.event;
      // Nothing loaded yet ⇒ this is the effect's initial run and `load()` is already on its way. Refetching
      // here would fire a second identical request on every visit.
      if (event === undefined) return;

      this.setCompositionValues(event, mode);
      this.load(undefined, false);
    });
  }

  /**
   * Is the loaded event a **virtual** one (#664)?
   *
   * Every sales-shaped sub-flow below is gated on this. The virtual page renders none of the buttons that
   * open them, so these guards can only be reached programmatically — by a spec, a story, or a future caller
   * — which is exactly why they exist: the server answers `E_EVENT_VIRTUAL_HAS_NO_SALES` to a download of a
   * virtual event, and "no request at all" is a better answer than a refusal nobody asked for.
   */
  protected get isVirtual(): boolean {
    return this.event?.kind === EventKind.Virtual;
  }

  protected get employeeSales_isOpen(): boolean {
    return this.employeeSales_data !== undefined;
  }

  protected get locationConfig_isOpen(): boolean {
    return this.locationConfig_data !== undefined && this.locationConfig_configurations !== undefined;
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.liveRefresh.complete();
  }

  ngOnInit(): void {
    this.liveRefresh_subscribe();
    this.preload(() => {
      this.load(() => {
        this.connect();
      });
    })
  }

  calculate(event: EventDetailsDto) {
    this.sortedLocationValuePairs = this.getSortedLocationValuePairs(event.locationValues);
  }

  OrderBy(sortedBy: Sorting, inverse: boolean): void {
    this.SortedBy = sortedBy;
    this.Inverse = inverse;

    if (this.event?.orders == undefined) return;

    this.event.orders = this.event.orders.sort((a, b) => {
      switch (this.SortedBy) {
        case Sorting.None:
          return 0;
        case Sorting.Date:
          return this.sorting_date(a.created, b.created);
        case Sorting.Price:
          return a.total - b.total;
        default:
          throw new Error("Unknown sorting type.");
      }
    });

    if (this.Inverse) this.event.orders = this.event.orders.reverse();
  }

  protected trackByFn(index: number, item: any) {
    return item.key;
  }

  protected getSortedLocationValuePairs(values: { [key: string]: { name: string, count: number, amount: number } }): {
    key: string,
    name: string,
    count: number,
    amount: number
  }[] {
    let result = this.getLocationValuePairs(values);
    return result.sort((a, b) => {
      return b.amount - a.amount;
    });
  }

  protected getLocationValuePairs(values: { [key: string]: { name: string, count: number, amount: number } }): {
    key: string,
    name: string,
    count: number,
    amount: number
  }[] {
    let result: { key: string, name: string, count: number, amount: number }[] = [];
    for (let key in values) {
      result.push({key: key, name: values[key].name, count: values[key].count, amount: values[key].amount});
    }
    return result;
  }

  protected getBookingValuePairs(values: { [key: number]: { count: number, amount: number } }): {
    key: number,
    count: number,
    amount: number
  }[] {
    let result: { key: number, count: number, amount: number }[] = [];
    for (let key in values) {
      result.push({key: parseInt(key), count: values[key].count, amount: values[key].amount});
    }
    return result;
  }

  protected employeeSales_close() {
    this.employeeSales_data = undefined;
  }

  protected employeeSales_open() {
    if (this.event === undefined || this.isVirtual) return; // #664: a virtual event has no employee sales
    this.employeeHttp.employeeOrders(this.event!.id).subscribe({
      next: employees => {
        // employee.total / order.total arrive server-computed via the central calculator (#142) —
        // no client-side re-derivation; the helpers below only prep the per-composition display prices.
        employees.forEach(employee => {
          employee.orders.forEach(order => {
            OrderHelper.setBusinessCaseBookingTypeValues(order);
            OrderHelper.setCompositionPrices(order);
            OrderHelper.setOrderBookingValues(order);
          })
        });
        this.employeeSales_data = employees;
      },
      error: err => {
        this.employeeSales_data = undefined;
        console.error(err);
        this.gas.createAndShow("Fehler", "Die Mitarbeiterverkäufe konnten nicht geladen werden.", AlertLevel.error);
      }
    })
  }

  protected supplierPopup_show() {
    this.supplierPopup_isVisible = true;
  }

  protected download(supplier: SupplierDto | undefined) {
    if (this.event === undefined || this.isVirtual) return; // #664: nothing to export from a virtual event
    this.eventHttp.download(this.event.id, supplier?.id ?? "")
      .withLoadingIndicator()
      .subscribe({
        next: file => {
          if (file.type == HttpEventType.Response) file.downloadFile();
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Download des Events.", AlertLevel.error);
        }
      })
  }

  protected locationConfig_close() {
    this.locationConfig_data = undefined;
    this.locationConfig_configurations = undefined;
  }

  protected locationConfig_open() {
    if (this.isVirtual) return; // #664: a virtual event carries no location configuration
    this.locationHttp.list()
      .subscribe({
        next: locations => {
          this.locationConfig_data = [];
          locations.forEach(location => {
            this.locationConfig_data!.push(new LocationConfigLinker(location));
          });

          // load configurations
          this.configurationHttp.list().subscribe({
            next: configurations => {
              this.locationConfig_configurations = configurations;

              this.locationConfig_data!.forEach(linker => {
                let index = this.event!.locationConfigLinkers.findIndex(x => x.location == linker.location.id);
                if (index < 0) return;
                linker.configuration = this.locationConfig_configurations!.find(x => x.id == this.event!.locationConfigLinkers[index].configuration);
              })
            },
            error: err => {
              this.locationConfig_configurations = undefined;
              console.error(err);
              this.gas.createAndShow("Fehler", "Die Preisprofile konnten nicht geladen werden.", AlertLevel.error);
            }
          })
        },
        error: err => {
          this.locationConfig_data = undefined;
          console.error(err)
          this.gas.createAndShow("Fehler", "Die Standorte konnten nicht geladen werden.", AlertLevel.error);
        }
      })
  }

  protected locationConfig_generalConfigurationChanged(configuration: ConfigurationDto | undefined) {
    this.locationConfig_data?.forEach(linker => {
      linker.configuration = configuration;
    })
  }

  protected locationConfig_save() {
    if (this.event === undefined || this.locationConfig_data === undefined) return;
    this.eventHttp.setLocationConfigs(
      this.event.id, this.locationConfig_data, this.pageSize, this.groupingParameter).subscribe({
      next: event => {
        this.apply(event);
        this.locationConfig_close();
        this.gas.createAndShow("Erfolg", "Die Preisprofile wurden erfolgreich gespeichert.", AlertLevel.success);
      },
      error: err => {
        console.error(err);
        this.gas.createAndShow("Fehler", "Die Preisprofile konnten nicht gespeichert werden.", AlertLevel.error);
      }
    })
  }

  protected settings_redirect() {
    if (this.event === undefined) return;
    this.router.navigate([AppRoutes.EventSettings], {queryParams: {id: this.event.id, last: this.last}}).then();
  }

  protected transaction_copyForeignId(order: EventDetailsOrder) {
    if (order.bookingType != BookingType.Card || !order.foreignTransactionId) return;
    copy(order.foreignTransactionId);
    this.gas.createAndShow("Information", `Transaktionsnummer ${order.foreignTransactionId} in Zwischenablage gespeichert.`, AlertLevel.info);
  }

  private preload(callback?: () => void) {
    // `takeUntilDestroyed` matters here (#408 / D2): this subscription re-runs `load() → connect()` on every
    // query-param change (clicking a "Verkaufspunkt" row sets `location`), and without a teardown it survived
    // the component — keeping a destroyed page's hub connection alive and refetching into it.
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.id = params.id;
        this.locationId = params.location;
        this.last = params.last ?? 10;

        if (this.id === undefined) return;

        if (callback !== undefined) callback();
      })
  }

  /**
   * Wires the live-refresh pipeline. Every hub notification goes through here — never straight into
   * {@link load} — so that at most one refetch is in flight and the newest response always wins.
   *
   * `throttleTime(..., {leading: true, trailing: true})` is load-bearing in BOTH directions:
   * - `leading` makes the first notification of a burst refresh immediately (the page stays snappy);
   * - `trailing` guarantees the burst's LAST notification still produces a refresh after the window closes.
   *   Dropping it (the rxjs default) is the subtle bug this whole pipeline could otherwise introduce: the
   *   final booking of a rush would be coalesced away and the tiles would sit one snapshot behind until the
   *   next notification — or forever, if the rush was the end of the event.
   */
  private liveRefresh_subscribe(): void {
    this.liveRefresh
      .pipe(
        throttleTime(LIVE_REFRESH_WINDOW_MS, asyncScheduler, {leading: true, trailing: true}),
        // switchMap: a newer notification CANCELS the in-flight refetch, so a slow older response can no
        // longer land after a newer one and roll the tiles back (#408 / D1). `catchError` sits INSIDE it so a
        // failed refresh never terminates the outer stream — the page keeps updating afterwards.
        switchMap(() => this.liveRefresh_request().pipe(
          catchError(err => {
            this.liveRefresh_onError(err);
            return EMPTY;
          })
        )),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(payload => this.liveRefresh_apply(payload));
  }

  /**
   * The read a live notification triggers: a DELTA once a cursor exists, a full snapshot otherwise — and a
   * full snapshot again whenever the server says the delta cannot be honoured.
   *
   * Never shows a loading indicator: this fires on every sale.
   *
   * The `fullReloadRequired` follow-up is chained INSIDE this observable rather than subscribed separately,
   * so the whole two-step exchange stays one unit of work under the outer `switchMap`. A newer notification
   * arriving mid-exchange cancels both halves — which is correct, because the newer request will do the same
   * thing from a cursor that has not moved.
   */
  private liveRefresh_request(): Observable<EventDetailsDto | EventOrderDelta> {
    const cursor = this.deltaCursor;
    if (cursor === undefined) return this.liveRefresh_fullRequest();

    return this.eventHttp.getEventDelta(this.id!, this.locationId ?? '', cursor, this.groupingParameter).pipe(
      switchMap(delta => delta.fullReloadRequired ? this.liveRefresh_fullRequest() : of(delta))
    );
  }

  /** The whole-event refetch: the pre-#723 live read, still used to seed and to recover a delta stream. */
  private liveRefresh_fullRequest(): Observable<EventDetailsDto> {
    return this.eventHttp.getExtendedEvent(
      this.id!, this.locationId ?? '', this.pageSize, this.groupingParameter);
  }

  /**
   * The active #534 grouping, in the shape the API binds (#716 / Task #722).
   *
   * Every read of this page sends it — the snapshot, the delta and the price-profile save alike — because the
   * card rows now come from the server. A request that forgot it would re-bucket the cards mid-session, which
   * looks like rows randomly merging rather than like a missing parameter.
   */
  private get groupingParameter(): CompositionGroupingParameter {
    return compositionGroupingParameter(this.grouping.mode());
  }

  /** Routes a live payload to the snapshot or the delta path. */
  private liveRefresh_apply(payload: EventDetailsDto | EventOrderDelta): void {
    this.liveRefresh_failures = 0;
    this.liveRefresh_alerted = false;

    // Discriminated on a field only the delta has. Deliberately NOT `instanceof EventDetailsDto`: these objects come
    // off the wire through `HttpClient`, which produces plain parsed JSON — nothing on this page is ever an
    // instance of the class that describes it, so an `instanceof` test here would be false for every snapshot
    // and silently route the whole live path into the delta branch.
    if (this.isDelta(payload)) {
      this.liveRefresh_applyDelta(payload);
      return;
    }

    this.liveRefresh_applySnapshot(payload);
  }

  /** Narrows a live payload to a delta. See {@link liveRefresh_apply} for why this is not an `instanceof`. */
  private isDelta(payload: EventDetailsDto | EventOrderDelta): payload is EventOrderDelta {
    return 'fullReloadRequired' in payload;
  }

  /**
   * Applies a live snapshot — unless it is degenerate, in which case the last good one is kept (#408 / D4).
   */
  private liveRefresh_applySnapshot(event: EventDetailsDto): void {
    if (this.isDegenerateSnapshot(event)) {
      // Deliberately silent towards the user: the previous (correct) numbers stay on screen and the next
      // notification refetches. Logged so the condition is visible in Sentry's breadcrumbs.
      //
      // The cursor is deliberately NOT adopted here: a payload we refused to believe must not become the
      // position we ask the next delta from, or the rows it described would never be requested again.
      console.warn("[event-details] Discarded a degenerate live snapshot (orders without compositions).");
      return;
    }

    this.apply(event);
  }

  /**
   * Merges a delta into the event on screen (#716 / Task #723), then — and only then — adopts its cursor.
   *
   * Three rules, in this order, and the order is the design:
   *
   * 1. **Refuse a degenerate payload first.** The same #408 defence the snapshot path applies, judged on the
   *    statistics, which are event-wide and exact on every delta. A refused delta advances nothing.
   * 2. **Merge by order id.** An id already held is overwritten; an id in `removedOrderIds` is dropped.
   *    Re-applying the same delta is therefore a no-op, which is what makes the server's at-least-once
   *    delivery safe — and at-least-once is not an accident but the only correct choice here, because a
   *    change's timestamp is fixed before its transaction commits and an exact cursor would step over a late
   *    commit forever. See the #716 ADR.
   * 3. **Advance the cursor last.** See {@link deltaCursor}. If anything above returns early or throws, the
   *    cursor stays put and the next notification asks for the same range again.
   *
   * The tiles are never derived from the merged list: {@link EventDetailsDto.orderStats} is replaced wholesale with
   * the server's aggregate over the WHOLE event, which is what keeps the numbers exact however bounded or
   * stale the visible rows happen to be.
   */
  private liveRefresh_applyDelta(delta: EventOrderDelta): void {
    const event = this.event;
    if (event === undefined) return; // nothing to merge into; the pending full load will seed it

    if (this.isDegenerateStats(event.orderStats, delta.stats)) {
      console.warn("[event-details] Discarded a degenerate live delta (event statistics dropped to zero).");
      return;
    }

    const byId = new Map(event.orders.map(order => [order.id, order]));
    delta.changed.forEach(order => byId.set(order.id, order));
    delta.removedOrderIds.forEach(id => byId.delete(id));

    // Newest first, `id` breaking ties — the same total order the server pages on (`Created DESC, Id DESC`),
    // so a merged list and a freshly loaded page agree about what "the newest 100" are. Re-sorted rather than
    // inserted in place because the user may have re-sorted the table via `OrderBy`.
    //
    // `new Date(...)` rather than a bare comparison: `created` is TYPED as a `Date` and is in fact an ISO
    // string at runtime, because `HttpClient` parses JSON and JSON has no date type. Comparing the raw values
    // happens to work for same-precision UTC strings and silently stops working for anything else.
    event.orders = [...byId.values()].sort((a, b) => {
      const delta = new Date(b.created).getTime() - new Date(a.created).getTime();
      return delta !== 0 ? delta : b.id.localeCompare(a.id);
    });

    event.orderStats = delta.stats;

    // The new position rides ON the event, so `apply` — the one place that writes {@link deltaCursor} —
    // commits the merged rows, the statistics and the cursor together, or none of them. Assigning the field
    // directly here would be a second write site, and the whole correctness argument rests on there being
    // exactly one.
    event.deltaCursor = delta.cursor;

    this.apply(event);
  }

  /**
   * A live refresh failed. Unlike the initial load this must NOT navigate away (#408 / D3) — a transient
   * gateway 429/502 during a sale window would otherwise throw the user off the page they are watching.
   * The last good snapshot stays; the refresh is retried with a doubling backoff.
   */
  private liveRefresh_onError(err: unknown): void {
    console.error(err);
    this.liveRefresh_failures++;

    if (this.liveRefresh_failures > LIVE_REFRESH_MAX_RETRIES) {
      // Give up retrying on our own; the next booking's notification will try again anyway.
      if (this.liveRefresh_alerted) return;
      this.liveRefresh_alerted = true;
      this.gas.createAndShow("Hinweis",
        "Die Live-Aktualisierung ist unterbrochen. Die angezeigten Werte sind möglicherweise nicht aktuell.",
        AlertLevel.warning);
      return;
    }

    const delay = LIVE_REFRESH_RETRY_BASE_MS * Math.pow(2, this.liveRefresh_failures - 1);
    timer(delay)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.liveRefresh.next());
  }

  /**
   * Is this snapshot one we must refuse in favour of what is already on screen?
   *
   * The shapes that qualify — and only while a populated snapshot exists, since an initial load renders
   * whatever the server says and an event really can be empty:
   *
   * - **nothing sold at all** although the current snapshot had sales — the tiles would all drop to `0`;
   * - **no composition lines anywhere**, although the current snapshot had them. That is exactly the payload
   *   Bug #408's torn split query returned, and it zeroes precisely the two composition-derived tiles
   *   („Sonstiges" / „Artikel") while the others survive.
   *
   * **Since #716 the guard judges the STATS, not the page** — and it had to change, or it would have broken
   * the feature that introduced it. The old version refused any snapshot with FEWER orders than the current
   * one, which is a perfectly ordinary payload once the list is bounded: switching from „Alle" to 100, or
   * simply reloading at 100 after a fuller page, would have been discarded as degenerate and logged as a
   * torn read. The tiles are what must never silently drop to zero, so the tiles' own source — the
   * server-side, event-wide aggregate — is what is compared. It is also the STRONGER test: it sees a
   * zeroing that a page of orders could not reveal at all.
   *
   * A payload without stats — which, since Feature #760, means only a virtual event (#664) or a pre-#716
   * server; it can no longer mean "the projection forgot them" — falls back to the original order-shaped
   * test, unchanged.
   */
  private isDegenerateSnapshot(event: EventDetailsDto): boolean {
    const current = this.event;
    if (current === undefined) return false;

    const currentStats = current.orderStats;
    const nextStats = event.orderStats;

    if (currentStats !== undefined && nextStats !== undefined) {
      return this.isDegenerateStats(currentStats, nextStats);
    }

    const currentOrders = current.orders?.length ?? 0;
    if (currentOrders === 0) return false; // nothing to protect

    if ((event.orders?.length ?? 0) === 0) return true;

    return this.compositionCount(current) > 0 && this.compositionCount(event) === 0;
  }

  /**
   * The #408 hold-last-good test, judged on the event-wide STATISTICS — the one thing that is complete on
   * every payload this page receives, snapshot or delta, and the one thing that must never silently drop to
   * zero while a venue is selling.
   *
   * Shared by both live paths on purpose. Under deltas it is the ONLY form the guard can take: a delta's row
   * list is bounded by what changed, so "fewer orders than before" is the normal case rather than the alarm,
   * and there is no longer any row-shaped signal left to judge.
   *
   * @param current the statistics on screen; `undefined` means nothing is being protected yet.
   * @param next the statistics that just arrived.
   */
  private isDegenerateStats(current: EventOrderStats | undefined, next: EventOrderStats | undefined): boolean {
    if (current === undefined || next === undefined) return false;
    if (current.orderCount === 0) return false; // nothing to protect
    if (next.orderCount === 0) return true;
    return this.statCompositionCount(current) > 0 && this.statCompositionCount(next) === 0;
  }

  /** Total number of booked article units across the WHOLE event, per the server's aggregate. */
  private statCompositionCount(stats: EventOrderStats): number {
    const sum = (buckets: { quantity: number }[]) => buckets.reduce((count, b) => count + b.quantity, 0);
    return sum(stats.compositions) + sum(stats.others);
  }

  /** Total number of composition lines across an event's LOADED orders (the pre-#716 fallback test). */
  private compositionCount(event: EventDetailsDto): number {
    return (event.orders ?? []).reduce((count, order) => count + (order.compositions?.length ?? 0), 0);
  }

  /**
   * The user picked a different „Angezeigte Transaktionen" size — reload the table at the new bound.
   *
   * The statistics do not depend on it, so nothing above the table moves; only the list gets longer or
   * shorter. `loader: true` because this is a direct response to a click and may take a moment at „Alle".
   */
  protected pageSize_change(value: number | number[] | undefined): void {
    const size = typeof value === 'number' ? value : DEFAULT_PAGE_SIZE;
    if (size === this.pageSize) return;

    this.pageSize = size;
    this.load();
  }

  /** How many transactions the table is currently showing out of how many the event holds (#716). */
  protected get loadedOrderCount(): number {
    return this.event?.orders?.length ?? 0;
  }

  /** The event's total transaction count, or the loaded count when the server shipped no statistics. */
  protected get totalOrderCount(): number {
    return this.event?.orderStats?.orderCount ?? this.loadedOrderCount;
  }

  /** Is the table showing only part of the event? Drives the „X von Y" hint under it. */
  protected get isOrderListBounded(): boolean {
    return this.loadedOrderCount < this.totalOrderCount;
  }

  /**
   * Should the page name the cost of „Alle" BEFORE the user picks it (Task #755)?
   *
   * At {@link LARGE_EVENT_TRANSACTION_THRESHOLD}+ transactions, „Alle" stops being a bigger version of the
   * same request and becomes a materially slower, multi-megabyte one (see the constant's own doc for the
   * measured numbers) — with no indication of that cost anywhere in the picker. The notice names the real,
   * server-reported total ({@link totalOrderCount}), never a guessed byte size.
   *
   * Hidden once „Alle" is actually the active {@link pageSize}: at that point the cost has already been paid
   * (or is being paid — see {@link load}, whose `loader: true` default already puts the app-wide blocking
   * spinner on screen for exactly this request, via `withLoadingIndicator()`), and a forward-looking "this
   * will take a while" no longer describes what is happening.
   */
  protected get showAllPageSizeNotice(): boolean {
    return this.totalOrderCount > LARGE_EVENT_TRANSACTION_THRESHOLD && this.pageSize !== PAGE_SIZE_ALL;
  }

  /**
   * Are the „Artikel" and „Sonstiges" rows currently a page-scoped fold rather than the server's event-wide
   * aggregate (#716 / Task #722)? Drives the two cards' `settling` affordance — and nothing else on the page.
   *
   * **This is derived from the data on screen, not from a request counter, and that is the whole design.** The
   * condition asked here is character for character the one {@link EventHelper} itself branches on when it
   * decides to fold the loaded page instead of reading `orderStats`: the snapshot in memory carries one grain,
   * the user is asking for the other. So the affordance is shown exactly while, and only while, the rows really
   * do describe the loaded page rather than the whole event — it cannot drift from the thing it describes.
   *
   * What that buys, case by case:
   *
   * - **A mode flip** turns it on SYNCHRONOUSLY, in the same tick as the click (the effect re-folds the rows
   *   and refetches; the snapshot still carries the old grain), and off again the moment a payload at the new
   *   grain is committed by {@link apply}.
   * - **A live-refresh delta cannot turn it on.** Every live read sends {@link groupingParameter}, i.e. the
   *   grain currently being displayed, and the server stamps what it aggregated onto `orderStats.grouping` —
   *   so a delta that lands during an ordinary sale matches by construction and the cards do not flicker on
   *   each booking. The one delta that does NOT match is one issued before a flip and answered after it, and
   *   in that case the rows genuinely ARE at the wrong grain: showing the affordance is the correct answer,
   *   and the flip's own refetch clears it.
   * - **The initial load** never shows it: the first request already asks for the persisted mode, and until it
   *   answers there is no `event` and therefore no card rendered at all.
   * - **A page-size change** never shows it: it re-reads at the same grain, so neither side of the comparison
   *   moves.
   * - **A pre-#716 server** (no `orderStats` at all) never shows it. The fold is permanent there rather than
   *   transient, and a progress line that never ends would be a lie.
   */
  protected get isCompositionGrainSettling(): boolean {
    const stats = this.event?.orderStats;
    if (stats === undefined) return false;
    // `?? 'Combined'` mirrors `EventHelper.compositionValues`: a payload that does not say which grain it
    // carries is a pre-#722 one, which aggregated at composition grain.
    return (stats.grouping ?? 'Combined') !== this.groupingParameter;
  }

  /**
   * Commits a snapshot to the view (aggregates first, so the tiles and the event swap in together).
   *
   * **This is also where a delta stream is seeded or re-seeded** (#716 / Task #723): every full snapshot that
   * reaches the screen carries the cursor it was taken at, so "full load, then deltas forever" has no seam a
   * change could fall through. A snapshot that was REFUSED never gets here, and therefore never moves the
   * cursor — which is the same rule the delta path follows, stated once for both.
   *
   * A snapshot without a cursor (a virtual event, a pre-#723 server) clears it, so the page falls back to
   * refetching in full rather than sending a stale position from a previous event.
   *
   * **All four tile dictionaries are re-derived here, from `event.orderStats`** (Bug #810). A delta replaces
   * `orderStats` and nothing else (see {@link liveRefresh_applyDelta}), so `apply()` is where new statistics
   * become what the cards render. Before #810 only the composition dictionaries ("Artikel", "Sonstiges") were
   * re-derived here. `bookingValues` and `locationValues` ("Transaktionstypen", "Verkaufspunkt") were set only
   * by `EventHttpService.prepare()` on a full load, so after a live delta those two cards kept the rows of the
   * previous full load until the page was reloaded.
   */
  private apply(event: EventDetailsDto): void {
    // #534: `EventHttpService` already aggregated the two composition summaries, but it cannot know the user's
    // grouping preference (it is a stateless mapping shared by every caller), so it always asks for the
    // `'combined'` shape. Re-derive them here for the active mode — on the initial load, on every live
    // refresh, and after a price-profile save, i.e. at every point a fresh snapshot enters the view.
    //
    // #716: "re-derive" is now almost always a straight read of the server's buckets — `EventHelper` folds the
    // loaded orders only when the payload's own grain (`orderStats.grouping`) is not the one being asked for.
    this.setCompositionValues(event, this.grouping.mode());
    // #810: the other two tile dictionaries need the same. Both replace their dictionary from scratch and never
    // add to the old one, so calling them on every apply is safe.
    EventHelper.setEventBookingValues(event);
    EventHelper.setLocationValues(event);
    this.calculate(event);
    this.event = event;
    this.deltaCursor = event.deltaCursor?.token;
  }

  /** Re-buckets both composition summaries of one snapshot for the given grouping mode (#534). */
  private setCompositionValues(event: EventDetailsDto, mode: CompositionGroupingMode): void {
    EventHelper.setNormalCompositionValues(event, mode);
    EventHelper.setOtherCompositionValues(event, mode);
  }

  private load(callback?: () => void, loader: boolean = true) {
    if (this.id === undefined) return;

    let load = this.eventHttp.getExtendedEvent(
      this.id, this.locationId ?? '', this.pageSize, this.groupingParameter);
    if (loader) load = load.withLoadingIndicator();
    load.subscribe({
      next: event => {
        this.apply(event);
        if (callback !== undefined) callback();
      },
      error: err => {
        console.error(err);
        this.router.navigate(['/' + AppRoutes.LocationModule + '/' + LocationRoutes.Events]).then(() => {
          this.gas.createAndShow("Fehler", "Das Event konnte nicht geladen werden.", AlertLevel.error);
        });
      }
    })

    if (this.locationId !== undefined && loader) {
      this.locationHttp.get(this.locationId)
        .withLoadingIndicator()
        .subscribe({
          next: location => {
            this.location = location;
          },
          error: err => {
            console.error(err);
            this.router.navigate(['/' + AppRoutes.LocationModule + '/' + LocationRoutes.EventDetails], {
              queryParams: {
                location: undefined
              },
              queryParamsHandling: 'merge'
            }).then(() => {
              this.gas.createAndShow("Fehler", "Der Standort konnte nicht geladen werden.", AlertLevel.error);
            });
          }
        })
    }
  }

  /**
   * Opens the event's SignalR hub connection — **idempotently** (#408 / D2).
   *
   * `connect()` runs from the `load()` callback, which runs on every query-param change, so it is called
   * again whenever the user drills into a "Verkaufspunkt". It used to build a fresh `HubConnection` and
   * overwrite `this.connection` with it: the previous connection was never stopped, kept receiving
   * `TransferEventData`, and `ngOnDestroy` could only stop the last one. Now an existing connection for the
   * same event is reused (the hub subscription is per event, and the location filter is applied on refetch,
   * not on the hub), and any connection for a DIFFERENT event is stopped before a new one is built.
   */
  private connect() {
    if (this.event === undefined) return;
    // #664: a virtual event never receives a booking, so there is nothing for the live-refresh pipeline to
    // hear. Opening the hub would cost a socket per visit and refetch an event that cannot change.
    if (this.isVirtual) return;
    if (this.connection !== undefined && this.connectedEventId === this.event.id) return;

    this.disconnect();
    this.connectedEventId = this.event.id;

    this.connection = new signalR.HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.Information)
      .withUrl(this.baseUrl + "/hubs/event?event=" + this.event.id, {
        accessTokenFactory: () => this.authenticationService.userValue!.jwt,
        //skipNegotiation: true,
        //transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .build();

    this.connection.start().then(() => {
      console.log("SignalR connection started.");
    }).catch(err => {
      return console.error(err.toString());
    })

    // The notification carries no payload — it only says "something changed". Hand it to the pipeline
    // instead of starting a refetch here, so bursts are coalesced and responses stay ordered.
    this.connection.on("TransferEventData", () => {
      if (this.id !== undefined) this.liveRefresh.next();
    })
  }

  /**
   * Stops the current hub connection, if any.
   *
   * The reference is cleared SYNCHRONOUSLY: `stop()` resolves later, and clearing it in the promise
   * callback used to null out whatever connection had been assigned in the meantime — which is precisely
   * what made `connect()` unsafe to call twice.
   */
  private disconnect(): void {
    const connection = this.connection;
    this.connection = undefined;
    this.connectedEventId = undefined;
    if (connection === undefined) return;

    connection.off("TransferEventData");
    connection.stop().then((r: any) => {
      console.log("SignalR disconnected: " + r);
    }, (error: any) => {
      console.error(error);
    })
  }

  private sorting_date(a: Date, b: Date): number {
    return a.getTime() - b.getTime();
  }
}
